import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { GeckoWeld } from 'types/assetModel/features/welds/geckoWelds'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

export interface GeckoConeRadialWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.ConeRadialWeld
  feature_geometry: {
    y_start: number
    y_end: number
    x: number
  }
}

export const NewGeckoConeRadialWeldJSON: GeckoConeRadialWeldJSON = {
  name: 'New Cone Radial Weld',
  type: GeckoFeatureModelType.ConeRadialWeld,
  feature_geometry: {
    y_start: 0,
    y_end: 0,
    x: 0,
  },
}

export class GeckoConeRadialWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.ConeRadialWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoConeRadialWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x as number,
      feature_geometry.y_start as number,
      feature_geometry.x as number,
      feature_geometry.y_end as number
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoConeRadialWeldJSON>
  ): Map<string, GeckoConeRadialWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoConeRadialWeld(componentID, weldID, recordsJson[weldID])
      )
      return resultObj
    }, new Map<string, GeckoConeRadialWeld>())
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoConeRadialWeldJSON
  ): GeckoConeRadialWeld {
    return new GeckoConeRadialWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generateConeRadialWeld(
      this.name,
      this.feature_geometry.x.min,
      this.feature_geometry.y.min,
      this.feature_geometry.y.max
    )
  }
}

export function generateConeRadialWeld(
  name: string | undefined,
  x: GeckoMeasurement,
  yStart: GeckoMeasurement,
  yEnd: GeckoMeasurement
): GeckoConeRadialWeldJSON {
  const coneRadialWeldJSON: GeckoConeRadialWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.ConeRadialWeld,
    feature_geometry: {
      x: x
        .convertTo(GeckoMeasurementUnit.Degrees)
        .toPrecision(assetModelPrecision).num,
      y_start: yStart
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y_end: yEnd
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return coneRadialWeldJSON
}

export const ConeRadialWeldFormFields = (weldJSON: GeckoConeRadialWeldJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: weldJSON.feature_geometry.x,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: weldJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: weldJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ConeRadialWeldJSONFromFormFields = (formFields: FormField[]) => {
  return generateConeRadialWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Degrees
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const ConeRadialWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ConeRadialWeld,
  title: 'Radial Welds',
  singularName: 'Radial Weld',
  fields: ['x', 'y_start', 'y_end'],
  sortBy: [{ fieldName: 'y_start', order: 'asc' }],
  disableEditing: true,
  tableMessage:
    'Adding / Editing of Radial Welds is disabled as they are automatically generated from Plates and/or Courses',
}
